import { useState, useEffect, useCallback } from 'react';

interface DurationInputProps {
  value: string;
  onChange: (value: string) => void;
  min?: number; // in seconds
  max?: number; // in seconds
  disabled?: boolean;
}

type DurationUnit = 's' | 'm' | 'h' | 'd';

const UNIT_LABELS: Record<DurationUnit, string> = {
  s: 'secondi',
  m: 'minuti',
  h: 'ore',
  d: 'giorni',
};

const UNIT_MULTIPLIERS: Record<DurationUnit, number> = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
};

function parseDuration(value: string): { num: number; unit: DurationUnit } {
  const match = value.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d)$/);
  if (match) {
    return { num: parseFloat(match[1]), unit: match[2] as DurationUnit };
  }
  // Fallback: raw number treated as seconds
  const num = parseFloat(value);
  if (!isNaN(num)) {
    // Pick best unit
    if (num >= 86400 && num % 86400 === 0) return { num: num / 86400, unit: 'd' };
    if (num >= 3600 && num % 3600 === 0) return { num: num / 3600, unit: 'h' };
    if (num >= 60 && num % 60 === 0) return { num: num / 60, unit: 'm' };
    return { num, unit: 's' };
  }
  return { num: 0, unit: 's' };
}

function toSeconds(num: number, unit: DurationUnit): number {
  return num * UNIT_MULTIPLIERS[unit];
}

export function DurationInput({ value, onChange, min, max, disabled }: DurationInputProps) {
  const parsed = parseDuration(value);
  const [num, setNum] = useState(parsed.num);
  const [unit, setUnit] = useState<DurationUnit>(parsed.unit);

  // Sync from parent
  useEffect(() => {
    const p = parseDuration(value);
    setNum(p.num);
    setUnit(p.unit);
  }, [value]);

  const emitChange = useCallback((newNum: number, newUnit: DurationUnit) => {
    onChange(`${newNum}${newUnit}`);
  }, [onChange]);

  const handleNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (isNaN(val)) return;
    setNum(val);
    emitChange(val, unit);
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value as DurationUnit;
    // Convert current value to new unit
    const totalSeconds = toSeconds(num, unit);
    const newNum = totalSeconds / UNIT_MULTIPLIERS[newUnit];
    setUnit(newUnit);
    setNum(newNum);
    emitChange(newNum, newUnit);
  };

  // Determine valid range in current unit
  const minInUnit = min !== undefined ? min / UNIT_MULTIPLIERS[unit] : undefined;
  const maxInUnit = max !== undefined ? max / UNIT_MULTIPLIERS[unit] : undefined;

  return (
    <div className="flex gap-2">
      <input
        type="number"
        value={num}
        onChange={handleNumChange}
        min={minInUnit}
        max={maxInUnit}
        step={unit === 's' ? 1 : 0.5}
        disabled={disabled}
        className="input w-24"
      />
      <select
        value={unit}
        onChange={handleUnitChange}
        disabled={disabled}
        className="input w-28"
      >
        {(Object.keys(UNIT_LABELS) as DurationUnit[]).map(u => (
          <option key={u} value={u}>{UNIT_LABELS[u]}</option>
        ))}
      </select>
    </div>
  );
}
